import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { clientApi } from '../api/client'
import { userApi } from '../api/user'
import { authStorage } from '../utils/authStorage'
import { urlUtils } from '../utils'
import { ClientErrorView } from '../components/ClientErrorView'
import type { Client, ClientLoadErrorType, PublicUser } from '../types'

const getInitials = (firstName: string, lastName: string): string => {
  const first = firstName?.charAt(0)?.toUpperCase() || ''
  const last = lastName?.charAt(0)?.toUpperCase() || ''
  return `${first}${last}` || '?'
}

export const AccountSessionView: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const clientId = searchParams.get('client_id')

  const [client, setClient] = useState<Client | null>(null)
  const [user, setUser] = useState<PublicUser | null>(null)
  const [clientLoadError, setClientLoadError] = useState<ClientLoadErrorType | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRedirecting, setIsRedirecting] = useState(false)

  useEffect(() => {
    const loadSession = async () => {
      if (!clientId) {
        setClientLoadError('missing_client_id')
        setIsLoading(false)
        return
      }

      const token = authStorage.getToken()
      if (!token) {
        navigate(`/login?client_id=${clientId}`, { replace: true })
        return
      }

      try {
        const [clientData, userData] = await Promise.all([
          clientApi.getClientByPublicId(clientId),
          userApi.getMe(),
        ])
        setClient(clientData)
        setUser(userData)
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status === 403 || status === 401) {
          authStorage.clearToken()
        }
        navigate(`/login?client_id=${clientId}`, { replace: true })
      } finally {
        setIsLoading(false)
      }
    }

    loadSession()
  }, [clientId, navigate])

  const handleContinueWithAccount = () => {
    if (!client || !user) return

    const token = authStorage.getToken()
    if (!token) {
      navigate(`/login?client_id=${clientId}`, { replace: true })
      return
    }

    setIsRedirecting(true)
    const redirectUrl = urlUtils.buildRedirectUrl(client.redirectUrl, {
      token: `Bearer ${token}`,
      user_id: user.id.toString(),
      email: user.email,
    })
    window.location.href = redirectUrl
  }

  const handleSwitchAccount = () => {
    navigate(`/login?client_id=${clientId}&switch_account=1`)
  }

  const handleCreateAccount = () => {
    navigate(`/signup?client_id=${clientId}`)
  }

  const handleLogout = () => {
    authStorage.clearToken()
    navigate(`/login?client_id=${clientId}`, { replace: true })
  }

  if (isLoading) {
    return (
      <div className="card fade-in">
        <div className="card-header">
          <div className="spinner"></div>
          <p className="card-loading-text">Chargement...</p>
        </div>
      </div>
    )
  }

  if (clientLoadError) {
    return <ClientErrorView type={clientLoadError} clientId={clientId} />
  }

  if (!client || !user) {
    return null
  }

  return (
    <div className="card fade-in">
      <div className="card-header">
        {client.logo && (
          <img
            src={client.logo}
            alt={client.title}
            style={{ height: '60px', marginBottom: '1rem' }}
          />
        )}
        <h1 className="card-title">Bon retour</h1>
        <p className="card-subtitle">
          Vous êtes déjà connecté à Olecio. Choisissez comment continuer vers {client.title}.
        </p>
      </div>

      <div className="account-card">
        <div className="account-avatar" aria-hidden="true">
          {getInitials(user.firstName, user.lastName)}
        </div>
        <div className="account-info">
          <p className="account-name">
            {user.firstName} {user.lastName}
          </p>
          <p className="account-email">{user.email}</p>
        </div>
      </div>

      <div className="account-actions">
        <button
          type="button"
          className="btn btn-primary btn-full"
          onClick={handleContinueWithAccount}
          disabled={isRedirecting}
        >
          {isRedirecting ? (
            <>
              <div className="spinner"></div>
              Redirection...
            </>
          ) : (
            'Continuer avec ce compte'
          )}
        </button>

        <button
          type="button"
          className="btn btn-secondary btn-full"
          onClick={handleSwitchAccount}
          disabled={isRedirecting}
        >
          Utiliser un autre compte
        </button>

        <button
          type="button"
          className="btn btn-outline btn-full"
          onClick={handleCreateAccount}
          disabled={isRedirecting}
        >
          Créer un compte
        </button>

        <button
          type="button"
          className="btn btn-ghost btn-full"
          onClick={handleLogout}
          disabled={isRedirecting}
        >
          Se déconnecter
        </button>
      </div>
    </div>
  )
}
